import { useState, useEffect, useRef, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { MapStyle, MAP_STYLES } from '../types';

mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_TOKEN || '';

export interface UseMapOptions {
  containerId?: string;
  initialStyle?: MapStyle;
  initialCenter?: [number, number];
  initialZoom?: number;
}

export interface UseMapReturn {
  map: mapboxgl.Map | null;
  mapContainerRef: React.RefObject<HTMLDivElement>;
  isLoaded: boolean;
  style: MapStyle;
  setStyle: (style: MapStyle) => void;
  flyTo: (center: [number, number], zoom?: number) => void;
  userLocation: [number, number] | null;
}

/**
 * Hook that initializes and manages a Mapbox GL map instance.
 * Adds navigation, geolocate, and scale controls.
 * Requests geolocation on mount and flies to user location if granted.
 */
export function useMap(options: UseMapOptions = {}): UseMapReturn {
  const {
    initialStyle = 'streets',
    initialCenter = [0, 20],
    initialZoom = 2,
  } = options;

  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [style, setStyleState] = useState<MapStyle>(initialStyle);
  const [userLocation, setUserLocation] = useState<[number, number] | null>(null);

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: MAP_STYLES[initialStyle],
      center: initialCenter,
      zoom: initialZoom,
      attributionControl: true,
    });

    // Add controls
    map.addControl(new mapboxgl.NavigationControl(), 'top-right');
    const geolocate = new mapboxgl.GeolocateControl({
      positionOptions: { enableHighAccuracy: true },
      trackUserLocation: true,
      showUserHeading: true,
    });
    map.addControl(geolocate, 'top-right');
    map.addControl(new mapboxgl.ScaleControl({ maxWidth: 100 }), 'bottom-left');

    map.on('load', () => {
      setIsLoaded(true);
      // Auto-trigger geolocation to show blue dot
      setTimeout(() => { geolocate.trigger(); }, 500);
    });

    mapRef.current = map;

    // Request geolocation on mount
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { longitude, latitude } = position.coords;
          const coords: [number, number] = [longitude, latitude];
          setUserLocation(coords);
          map.flyTo({ center: coords, zoom: 13, duration: 2000 });
        },
        () => {
          // Denied or error - keep default world view
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    }

    return () => {
      map.remove();
      mapRef.current = null;
      setIsLoaded(false);
    };
  }, []);

  const setStyle = useCallback((newStyle: MapStyle) => {
    if (mapRef.current) {
      mapRef.current.setStyle(MAP_STYLES[newStyle]);
      setStyleState(newStyle);
    }
  }, []);

  const flyTo = useCallback((center: [number, number], zoom?: number) => {
    if (mapRef.current) {
      mapRef.current.flyTo({ center, zoom: zoom || 13, duration: 2000 });
    }
  }, []);

  return {
    map: mapRef.current,
    mapContainerRef: mapContainerRef as React.RefObject<HTMLDivElement>,
    isLoaded,
    style,
    setStyle,
    flyTo,
    userLocation,
  };
}
